import {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";

const STORAGE_KEY = "findYourRoomFormData";

const defaultFormData = {
  step: "",
  city: null,
  moveInDate: null,
  duration: [],
  price: [],
  space: [],
  roomCategorySlug: null,
  suits: null,
  name: "",
  email: "",
  phone: "",
  countryCode: "+44",
  agreedToTerms: false,
};

const FormContext = createContext({
  formData: {
    step: "",
    city: null,
    moveInDate: null,
    duration: [],
    price: [],
    space: [],
    name: "",
    email: "",
    phone: "",
    countryCode: "+44",
    agreedToTerms: false,
  },
  updateFormData: () => {},
  resetFormData: () => {},
  submitForm: () => {},
  resetForm: () => {},
});

export const FormProvider = ({ children }) => {
  // Define the ordered steps in the flow (earliest -> latest)
  const STEP_ORDER = [
    "city",
    "moveInDate",
    "duration",
    "price",
    "space",
    "roomCategorySlug",
    "suits",
  ];

  // Fields we should never clear when step values change
  const PRESERVED_FIELDS = new Set(["name", "email", "phone","countryCode"]);

  const isDeepEqual = (a, b) => {
    if (a === b) return true;
    const ta = typeof a;
    const tb = typeof b;
    if ((ta === "object" && a !== null) || (tb === "object" && b !== null)) {
      try {
        return JSON.stringify(a) === JSON.stringify(b);
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  const loadFormDataFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultFormData, ...parsed };
      }
    } catch (error) {
      console.error("Error loading form data from localStorage:", error);
    }
    return defaultFormData;
  };

  const [formData, setFormData] = useState(loadFormDataFromStorage);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (error) {
      console.error("Error saving form data to localStorage:", error);
    }
  }, [formData]);

  const updateFormData = (field, value) => {
    setFormData((prev) => {
      if (isDeepEqual(prev?.[field], value)) return prev;
      const next = { ...prev, [field]: value };
      const changedStepIndex = STEP_ORDER.indexOf(field);
      if (changedStepIndex !== -1) {
        for (let i = changedStepIndex + 1; i < STEP_ORDER.length; i += 1) {
          const key = STEP_ORDER[i];
          if (PRESERVED_FIELDS.has(key)) continue;
          next[key] = defaultFormData[key];
        }
      }
      return next;
    });
  };

  const resetForm = useCallback(() => {
    setFormData({
      city: null,
      moveInDate: null,
    });
  }, []);

  const submitForm = () => {
    // Here you can add your form submission logic
    // For example, send data to an API
    return formData;
  };

  return (
    <FormContext.Provider
      value={{ formData, updateFormData, submitForm, resetForm }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormData = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormData must be used within a FormProvider");
  }
  return context;
};
